"use client";
// components/QuickShop.jsx
import FilterDrawer from "./FilterDrawer";
import { useState, useEffect, useCallback } from "react";
import QuickShopHeader from "./QuickShopHeader";

const CATEGORY_EMOJI = {
  "Art Supplies":             "🎨",
  "Books":                    "📚",
  "Classroom Basics":         "🏫",
  "Educational Games":        "🎮",
  "Food, Clothing & Hygiene": "🍎",
  "Instructional Technology": "🔌",
  "STEM":                     "🔬",
  "Decor":                    "🌟",
  "Furniture":                "🪑",
  "Other":                    "📦",
};

const EXCLUDED_CATEGORIES = ["Food, Clothing & Hygiene", "Other"];


const TABS = [
  { label: "Under $5",  minPrice: 0,  maxPrice: 5    },
  { label: "$5 - $10",  minPrice: 5,  maxPrice: 10   },
  { label: "$10 - $20", minPrice: 10, maxPrice: 20   },
  { label: "All Items", minPrice: 0,  maxPrice: 9999 },
];

const DEFAULT_QS_FILTERS = {
  searchTerm:    "",
  categories:    [],
  states:        [],
  grades:        [],
  gradeGroupLabels: [],
  itemMinPrice:  0,
  itemMaxPrice:  500,
  minEnrollment: 0,
  maxEnrollment: 2000,
  minLunchPct:   0,
  maxLunchPct:   100,
};

function ItemTile({ item }) {
  const [hovered, setHovered] = useState(false);
  const estimatedPrice = Math.round(item.price);
  const gradeLabel = g => {
    if (!g) return null;
    if (String(g) === "PK") return "Pre-K";
    if (String(g) === "K")  return "Kinder";
    return `Grade ${g}`;
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: `2px solid ${hovered ? "#f97316" : "#fde68a"}`,
        borderRadius: 18, padding: "16px 14px 14px",
        display: "flex", flexDirection: "column", gap: 8,
        boxShadow: hovered ? "4px 4px 0 #f97316" : "3px 3px 0 #fde68a",
        transition: "all .15s", position: "relative",
      }}
    >
      <div style={{
        position: "absolute", top: -10, right: 12,
        background: "#ffffff", color: "#7c2d12",
        fontWeight: 900, fontSize: 15, padding: "2px 10px",
        borderRadius: 999, boxShadow: "2px 2px 2px 2px #ca8a04",
        display: "flex", alignItems: "baseline", gap: 3,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.9 }}>est.</span>
        ${estimatedPrice}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>
        {CATEGORY_EMOJI[item.category] || "📦"} {item.category}
      </div>

      <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", lineHeight: 1.3, flex: 1 }}>
        {item.product_title}
      </div>

      <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5, paddingTop: 6, borderTop: "1px dashed #fde68a" }}>
        <div style={{ fontWeight: 700, color: "#475569" }}>{item.teacher.name}</div>
        <div>{item.teacher.schoolName}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          {item.teacher.city}, {item.teacher.state}
          {item.teacher.grade && (
            <span style={{
              background: "#fef9c3", border: "1px solid #fde047",
              borderRadius: 999, padding: "0px 6px", fontSize: 10, fontWeight: 700, color: "#854d0e",
            }}>
              {gradeLabel(item.teacher.grade)}
            </span>
          )}
        </div>
      </div>

      <a href={item.teacher.wishlistUrl || "#"} target="_blank" rel="noopener noreferrer"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "4px 16px", width: "fit-content", margin: "0 0 0 auto",
          background: "#ffffff", color: "#7c2d12", fontWeight: 900, fontSize: 13,
          borderRadius: 10, textDecoration: "none",
          boxShadow: hovered ? "2px 2px 2px 2px #991b1b" : "2px 2px 2px 2px #ca8a04",
          transition: "all .15s",
        }}
      >
        🎁 Shop this list
      </a>
    </div>
  );
}

function SkeletonTile() {
  return (
    <div style={{
      background: "#fff", border: "2px solid #fde68a", borderRadius: 18,
      padding: "16px 14px 14px", display: "flex", flexDirection: "column", gap: 10,
      boxShadow: "3px 3px 0 #fde68a",
    }}>
      {[70, 90, 60, 100, 40].map((w, i) => (
        <div key={i} style={{
          height: i === 2 ? 40 : 12, width: `${w}%`,
          background: "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
          borderRadius: 6, backgroundSize: "200% 100%", animation: "shimmer 1.2s infinite",
        }} />
      ))}
    </div>
  );
}

export default function QuickShop() {
  const [activeTab,   setActiveTab]   = useState(0);
  const [filterOpen,  setFilterOpen]  = useState(false);
  const [qsFilters,   setQsFilters]   = useState(DEFAULT_QS_FILTERS);
  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset,      setOffset]      = useState(0);

  const LIMIT = 40;

  const fetchItems = useCallback(async (tabIndex, currentOffset, append, filters) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    const isManualPriceActive = filters.itemMinPrice > 0 || filters.itemMaxPrice < 500;
    const finalMinPrice = isManualPriceActive ? filters.itemMinPrice : TABS[tabIndex].minPrice;
    const finalMaxPrice = isManualPriceActive ? filters.itemMaxPrice : TABS[tabIndex].maxPrice;
    const effectiveCategories = filters.categories.length > 0
    ? filters.categories.filter(c => !EXCLUDED_CATEGORIES.includes(c))
    : Object.keys(CATEGORY_EMOJI).filter(c => !EXCLUDED_CATEGORIES.includes(c));

    try {
      const params = new URLSearchParams({
        searchTerm:    filters.searchTerm || "",
        limit:         LIMIT,
        offset:        currentOffset,
        categories:    effectiveCategories.join(","),
        states:        filters.states.join(","),
        grades:        filters.grades.join(","),
        itemMinPrice:  finalMinPrice,
        itemMaxPrice:  finalMaxPrice,
        minEnrollment: filters.minEnrollment || 0,
        maxEnrollment: filters.maxEnrollment || 2000,
        minLunchPct:   filters.minLunchPct   || 0,
        maxLunchPct:   filters.maxLunchPct   || 100,
      });
      const res  = await fetch(`/api/quickshop?${params}`);
      const data = await res.json();
      setItems(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setTotal(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setOffset(0);
    setItems([]);
    fetchItems(activeTab, 0, false, qsFilters);
  }, [activeTab, qsFilters, fetchItems]);

  const loadMore = () => {
    const next = offset + LIMIT;
    setOffset(next);
    fetchItems(activeTab, next, true, qsFilters);
  };

  const activeFilterCount =
    qsFilters.categories.length + qsFilters.states.length + qsFilters.grades.length +
    (qsFilters.itemMinPrice > 0 || qsFilters.itemMaxPrice < 500 ? 1 : 0) +
    (qsFilters.minEnrollment > 0 || qsFilters.maxEnrollment < 2000 ? 1 : 0) +
    (qsFilters.minLunchPct > 0 || qsFilters.maxLunchPct < 100 ? 1 : 0);

  return (
    <section style={{ padding: "0 12px 48px", background: "#fffbf5", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>

        <QuickShopHeader />

        {/* Tabs + filter */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {TABS.map((tab, i) => (
            <button key={i} onClick={() => setActiveTab(i)} style={{
              padding: "8px 16px", borderRadius: 999, cursor: "pointer",
              fontSize: 13, fontWeight: 800, transition: "all .15s",
              background: activeTab === i ? "linear-gradient(135deg, #f97316, #dc2626)" : "#fff",
              color:      activeTab === i ? "#fff" : "#92400e",
              boxShadow:  activeTab === i ? "0 4px 0 #991b1b" : "0 3px 0 #fde68a",
              border:     activeTab === i ? "2px solid transparent" : "2px solid #fde68a",
              transform:  activeTab === i ? "translateY(1px)" : "none",
            }}>
              {tab.label}
            </button>
          ))}
          <div style={{ width: 1, height: 24, background: "#fde68a", margin: "0 2px" }} />
          <button onClick={() => setFilterOpen(o => !o)} style={{
            padding: "8px 16px", borderRadius: 999, cursor: "pointer",
            fontSize: 13, fontWeight: 800,
            display: "flex", alignItems: "center", gap: 6,
            background: filterOpen || activeFilterCount > 0 ? "#1e293b" : "#fff9e6",
            color:      filterOpen || activeFilterCount > 0 ? "#fff"    : "#92400e",
            border:     filterOpen || activeFilterCount > 0 ? "2px solid #1e293b" : "2px solid #fde68a",
            boxShadow:  "0 3px 0 #fde68a", transition: "all .15s",
          }}>
            🎯 Filter
            {activeFilterCount > 0 && (
              <span style={{
                background: "#f97316", color: "#fff", borderRadius: "50%",
                width: 18, height: 18, fontSize: 10, fontWeight: 900,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{activeFilterCount}</span>
            )}
            <span style={{ fontSize: 10, opacity: 0.7 }}>{filterOpen ? "▲" : "▼"}</span>
          </button>
        </div>

        <div style={{ maxWidth: 640, margin: "0 auto 12px" }}>
          <FilterDrawer
            open={filterOpen}
            filters={qsFilters}
            onChange={f => {
              setQsFilters(f);
              if (f.itemMinPrice > 0 || f.itemMaxPrice < 500) setActiveTab(3);
            }}
            DEFAULT_QS_FILTERS={DEFAULT_QS_FILTERS}
          />
        </div>

        {/* ── Responsive grid ── */}
        <div className="quickshop-grid">
          {loading
            ? Array(8).fill(0).map((_, i) => <SkeletonTile key={i} />)
            : items.map(item => <ItemTile key={`${item.schoolID}-${item.itemID}`} item={item} />)
          }
        </div>

        {!loading && items.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎁</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>No items match your filters</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Try adjusting the price tab or clearing filters</div>
          </div>
        )}

        {hasMore && !loading && (
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <button onClick={loadMore} disabled={loadingMore} style={{
              padding: "12px 36px", borderRadius: 999,
              border: "2px solid #fde68a", background: "#fff",
              color: "#92400e", fontWeight: 800, fontSize: 14,
              cursor: "pointer", boxShadow: "0 3px 0 #fde68a",
              opacity: loadingMore ? 0.6 : 1,
            }}>
              {loadingMore ? "Loading..." : `Load More (${total - items.length} remaining)`}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .quickshop-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        @media (max-width: 1024px) {
          .quickshop-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 640px) {
          .quickshop-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
        }
        input[type=range] { pointer-events: none; }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none; pointer-events: all;
          width: 18px; height: 18px; border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #dc2626);
          border: 2px solid #fff; cursor: pointer;
        }
        input[type=range]::-moz-range-thumb {
          pointer-events: all; width: 18px; height: 18px; border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #dc2626);
          border: 2px solid #fff; cursor: pointer;
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
}